import React from "react";
import { useNavigate } from "react-router-dom";

// Shared building blocks for dashboard-style pages (Core's DashboardPage,
// Finance's FinanceDashboardPage, and future per-module dashboards per
// .claude/specs/per-module-dashboards.md).

export function StatCard({ label, value, color, icon, to, subtitle }: {
  label: string; value: number | string; color: string;
  icon: React.ReactNode; to: string; subtitle?: string;
}) {
  const navigate = useNavigate();
  return (
    // Real <button>, not a <div onClick> — keyboard users can Tab to it and
    // activate it. Hover/focus lift lives in responsive.css (.stat-card /
    // .stat-card:hover, :focus-visible), not inline style + JS handlers —
    // an inline boxShadow would otherwise beat any external :hover rule.
    <button type="button" onClick={() => navigate(to)} className="stat-card" style={{
      background: "white", borderRadius: 10, padding: "20px 24px",
      border: "1px solid #e2e8f0",
      // maxWidth matters now that the module dashboards no longer cap the page:
      // flex-grow would otherwise stretch three tiles to ~617px each on a 1900px
      // screen, which is a stat tile that is almost entirely whitespace.
      cursor: "pointer", flex: "1 1 160px", minWidth: 150, maxWidth: 340,
      boxSizing: "border-box" as const,
      position: "relative", overflow: "hidden",
      textAlign: "left", fontFamily: "inherit", appearance: "none",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: "10px 10px 0 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1, letterSpacing: -1 }}>{value}</div>
          <div style={{ fontSize: 13, color: "#334155", marginTop: 5, fontWeight: 600 }}>{label}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{subtitle}</div>}
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color, background: `${color}15` }}>
          {icon}
        </div>
      </div>
    </button>
  );
}

export function QuickAction({ label, icon, to, color }: {
  label: string; icon: React.ReactNode; to: string; color: string;
}) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "10px 16px", minHeight: 44, background: "white",
      border: `1.5px solid ${color}22`, borderRadius: 10,
      color, fontSize: 13, fontWeight: 600, cursor: "pointer",
      transition: "background 0.15s, border-color 0.15s",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = `${color}08`; e.currentTarget.style.borderColor = `${color}55`; }}
      onMouseLeave={e => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = `${color}22`; }}
    >
      {icon}
      {label}
    </button>
  );
}
